import { HtmlHTMLAttributes, useEffect, useRef, useState } from "react";
import { Checkbox, Icon, Select, Spinner } from "../../../components";
import * as label from '../../../shared/util/label';
import * as constant from '../../../shared/util/constant';
import { PanelMenu } from 'primereact/panelmenu';
import TransactionFilter from "../../model/TransactionFilter";
import { SelectOption } from "../../../shared/components/input";
import PolicyMappingDataService from "../PolicyMappingDataService"
import Logger from "../../../shared/services/Logger";
import CarrierMappingSnapshot from "../../model/CarrierData";
import { MenuItem } from "primereact/menuitem";
import { CarrierDataViewHeaderModel } from "../../model/CarrierDataViewModel"
import '../LeftPanel/leftPanel.scss'
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../shared/store";
import { updateCarrier } from "../../Unmatched-Policy-slice/Carrier-List-slice";
import { Badge } from 'primereact/badge';
import CustomerDataSnapshot from "../../model/CustomerDataSnapshot";
import React from "react";
import 'primeicons/primeicons.css';
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { TransactionMetadata } from "../../model/TransactionMetadata";
import moment from 'moment';

const FILTER_OPTIONS = [
  { label: constant.AssignmentscreenFilter.FILTER.CARRIER.label, value: constant.AssignmentscreenFilter.FILTER.CARRIER.value },
  { label: constant.AssignmentscreenFilter.FILTER.TRANSACTION.label, value: constant.AssignmentscreenFilter.FILTER.TRANSACTION.value },
  { label: constant.AssignmentscreenFilter.FILTER.CS2_ENTRY_DATE.label, value: constant.AssignmentscreenFilter.FILTER.CS2_ENTRY_DATE.value },
  { label: constant.AssignmentscreenFilter.FILTER.TRANSACTION_EFFECTIVE_DATE.label, value: constant.AssignmentscreenFilter.FILTER.TRANSACTION_EFFECTIVE_DATE.value },
  { label: constant.AssignmentscreenFilter.FILTER.TRANSACTION_TERM_DATES.label, value: constant.AssignmentscreenFilter.FILTER.TRANSACTION_TERM_DATES.value },
  { label: constant.AssignmentscreenFilter.FILTER.POLICY_NUMBER.label, value: constant.AssignmentscreenFilter.FILTER.POLICY_NUMBER.value },
  { label: constant.AssignmentscreenFilter.FILTER.ADDRESS.label, value: constant.AssignmentscreenFilter.FILTER.ADDRESS.value },
];

interface DynamicFilter {
  id: number;
  filterType: string;
  value: string;
  valueTo: string;
  valueHasInvalidDateInput?: boolean;
  valueToHasInvalidDateInput?: boolean;
}

type DynamicFilterField = 'value' | 'valueTo';
type FilterValidationError = Partial<Record<DynamicFilterField, string>>;

const FILTER_VALIDATION_RULES: Record<string, { minLength: number; minLengthMessage: string }> = {
  policyNumber: { minLength: 3, minLengthMessage: 'Minimum 3 characters are required' },
  address: { minLength: 2, minLengthMessage: 'Minimum 2 characters are required' },
};

const DATE_RANGE_FILTER_TYPES = ['effectiveTermDates', 'transactionEffectiveDate'];
const DATE_FILTER_TYPES = [...DATE_RANGE_FILTER_TYPES, 'cs2EntryDate'];

const isEmptyDateValue = (value: string) => {
  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue.length === 0 || normalizedValue === 'mm/dd/yyyy';
};

const getDateOnlyValue = (value: string) => {
  const date = moment(value, 'YYYY-MM-DD', true);
  return date.isValid() ? date.startOf('day') : null;
};

const validateDateField = (value: string, hasInvalidDateInput = false) => {
  if (hasInvalidDateInput || (!isEmptyDateValue(value) && getDateOnlyValue(value) === null)) {
    return 'Date is invalid';
  }
  if (isEmptyDateValue(value)) {
    return 'Field cannot be left blank';
  }
  return '';
};

const validateDateFilter = (filterRow: DynamicFilter): FilterValidationError => {
  const errors: FilterValidationError = {};
  const fromDate = getDateOnlyValue(filterRow.value);
  const toDate = getDateOnlyValue(filterRow.valueTo);
  const fromDateError = validateDateField(filterRow.value, filterRow.valueHasInvalidDateInput);
  const toDateError = validateDateField(filterRow.valueTo, filterRow.valueToHasInvalidDateInput);
  if (fromDateError) errors.value = fromDateError;
  if (toDateError) errors.valueTo = toDateError;
  if (fromDate !== null && toDate !== null && fromDate.isAfter(toDate)) {
    errors.value = 'Invalid date range';
    errors.valueTo = 'Invalid date range';
  }
  return errors;
};

const validateDynamicFilter = (filterRow: DynamicFilter): FilterValidationError => {
  if (DATE_RANGE_FILTER_TYPES.includes(filterRow.filterType)) {
    return validateDateFilter(filterRow);
  }
  if (filterRow.filterType === 'cs2EntryDate') {
    const validationError = validateDateField(filterRow.value, filterRow.valueHasInvalidDateInput);
    return validationError ? { value: validationError } : {};
  }
  const validationRule = FILTER_VALIDATION_RULES[filterRow.filterType];
  if (!validationRule) {
    return {};
  }
  if (filterRow.value.trim().length === 0) {
    return {};
  }
  if (filterRow.value.length < validationRule.minLength) {
    return { value: validationRule.minLengthMessage };
  }
  return {};
};

const validateDynamicFilters = (filters: DynamicFilter[]) => {
  return filters.reduce<Record<number, FilterValidationError>>((errors, filterRow) => {
    const filterErrors = validateDynamicFilter(filterRow);
    if (Object.keys(filterErrors).length > 0) {
      errors[filterRow.id] = filterErrors;
    }
    return errors;
  }, {});
};

interface Props {
  setInsuredSummary: (CustomerDataSnapshot) => void,
  setTransactionSummaryList: ([]) => void,
  setAtmsCustomerList: ([]) => void,
  refreshCounter?: number,
  autoAdvanceCounter?: number,
  hasUIComponentViewAccess?: boolean,
  hasUIComponentReadAccess?: boolean,
  selectedTransactionIds?: string[],
  setSelectedTransactionIds?: (ids: string[]) => void,
  setTransactionMetadataList?: (list: TransactionMetadata[]) => void,
}

const UnmatchedLeftPanel = (props: Props) => {

  const initalFilterState: TransactionFilter = {
    transactionType: '',
    processStatus: ['M','E'],
    insuredName:''
  }

  const InitialInsuredSummary: CustomerDataSnapshot = {
    cycleBusinessPurposeCode: '',
    insuredsName: '',
    carrierName: '',
    id: constant.DEFAULT_GUID,
    processStatus:''
  }

  const initialMenuItemList: MenuItem[] = [];

  const [transactionTypes, setTransactionTypes] = useState<SelectOption[]>([]);
  const [filter, setTransactionFilter] = useState<TransactionFilter>(initalFilterState);
  const [carrierHeaderlist, setCarriers] = useState<MenuItem[]>([]);
  const policyMappingDataService = new PolicyMappingDataService();
  const [isClosed, setIsClosed] = useState<boolean>(true);
  const [selectedCarrier, setSelectedCarrier] = useState<string>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeItem, setActiveItem] = useState<string>(null);
  const prevActiveItemRef = useRef<string>(null);
  const selectedTransactionIdsRef = useRef<string[]>([]);
  const carrierHeaderlistRef = useRef<MenuItem[]>([]);
  const selectedCarrierRef = useRef<string>(null);
  const selectedCarrierIndexRef = useRef<number>(-1);
  const selectedTransactionIndexRef = useRef<number | null>(null);
  const skipSelectedCarrierLoadRef = useRef(false);
  const previousAutoAdvanceCounterRef = useRef(0);
  const [dynamicFilters, setDynamicFilters] = useState<DynamicFilter[]>([]);
  const [filterValidationErrors, setFilterValidationErrors] = useState<Record<number, FilterValidationError>>({});
  const filterIdRef = useRef(0);
  const [totalTransactionCount, setTotalTransactionCount] = useState<number>(0);

  const addFilterRow = () => {
    filterIdRef.current += 1;
    setDynamicFilters(prev => [...prev, { id: filterIdRef.current, filterType: '', value: '', valueTo: '' }]);
  };

  const removeFilterRow = (id: number) => {
    setFilterValidationErrors(prev => {
      const { [id]: removedError, ...remainingErrors } = prev;
      return remainingErrors;
    });
    setDynamicFilters(prev => {
      const removedFilter = prev.find(f => f.id === id);
      if (removedFilter?.filterType === 'transactions') {
        setTransactionFilter(current => ({
          ...current,
          transactionType: ''
        }));
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const updateFilterRow = (id: number, field: keyof DynamicFilter, value: string, hasInvalidDateInput = false) => {
    setFilterValidationErrors(prev => {
      if (field === 'filterType') {
        const { [id]: removedError, ...remainingErrors } = prev;
        return remainingErrors;
      }
      const currentFilter = dynamicFilters.find(filterRow => filterRow.id === id);
      if (!currentFilter) {
        return prev;
      }
      const shouldValidate = Boolean(prev[id]) ||
        (DATE_FILTER_TYPES.includes(currentFilter.filterType) && (field === 'value' || field === 'valueTo'));
      if (!shouldValidate) {
        return prev;
      }
      const updatedFilter = { ...currentFilter, [field]: value };
      if (field === 'value') {
        updatedFilter.valueHasInvalidDateInput = hasInvalidDateInput;
      } else if (field === 'valueTo') {
        updatedFilter.valueToHasInvalidDateInput = hasInvalidDateInput;
      }
      const validationErrors = validateDynamicFilter(updatedFilter);
      if (Object.keys(validationErrors).length > 0) {
        return { ...prev, [id]: validationErrors };
      }
      const { [id]: removedError, ...remainingErrors } = prev;
      return remainingErrors;
    });
    if (field === 'filterType') {
      if (value === 'effectiveTermDates' || value === 'transactionEffectiveDate') {
        const today = new Date();
        if (value === 'transactionEffectiveDate') {
          const fromDate = new Date(today);
          fromDate.setFullYear(today.getFullYear() - 1);
          const fromDateValue = fromDate.toISOString().split('T')[0];
          const toDateValue = today.toISOString().split('T')[0];
          setDynamicFilters(prev => prev.map(f => f.id === id ? { ...f, filterType: value, value: fromDateValue, valueTo: toDateValue, valueHasInvalidDateInput: false, valueToHasInvalidDateInput: false } : f));
        } else {
          const oneYearLater = new Date(today);
          oneYearLater.setFullYear(today.getFullYear() + 1);
          const fromDate = today.toISOString().split('T')[0];
          const toDate = oneYearLater.toISOString().split('T')[0];
          setDynamicFilters(prev => prev.map(f => f.id === id ? { ...f, filterType: value, value: fromDate, valueTo: toDate, valueHasInvalidDateInput: false, valueToHasInvalidDateInput: false } : f));
        }
      } else {
        setDynamicFilters(prev => prev.map(f => f.id === id ? { ...f, filterType: value, value: '', valueTo: '', valueHasInvalidDateInput: false, valueToHasInvalidDateInput: false } : f));
      }
    } else {
      setDynamicFilters(prev => prev.map(f => {
        if (f.id !== id) {
          return f;
        }
        if (field === 'value') {
          return { ...f, value, valueHasInvalidDateInput: hasInvalidDateInput };
        }
        if (field === 'valueTo') {
          return { ...f, valueTo: value, valueToHasInvalidDateInput: hasInvalidDateInput };
        }
        return { ...f, [field]: value };
      }));
    }
  };

  const getActiveTransactionType = () => {
    return dynamicFilters.find(f => f.filterType === 'transactions')?.value || '';
  };

  const getFilterValue = (type: string) => {
    return dynamicFilters.find(f => f.filterType === type)?.value || '';
  };

  const getFilterValueTo = (type: string) => {
    return dynamicFilters.find(f => f.filterType === type)?.valueTo || '';
  };

  const onFieldChange = (name: string, value: string) => {
    setTransactionFilter({
      ...filter,
      [name]: value,
    })
    
  }


  useEffect(() => {
    policyMappingDataService.getTransactionTypes().then(res => {
      setTransactionTypes([{ label: 'All Transactions', value: '', isInactive: false }, ...res]);
    })
  }, []);

  // Keep ref in sync so InsuredTemplate (captured in PanelMenu items) reads current values
  useEffect(() => {
    selectedTransactionIdsRef.current = props.selectedTransactionIds || [];
  }, [props.selectedTransactionIds]);

  useEffect(() => {
    carrierHeaderlistRef.current = carrierHeaderlist;
  }, [carrierHeaderlist]);

  // Force PanelMenu to re-render templates when checkbox-related state changes
  useEffect(() => {
    setCarriers(prev => [...prev]);
  }, [props.selectedTransactionIds]);

   useEffect(() => {
        const prevActiveItem = prevActiveItemRef.current;
        if (prevActiveItem && prevActiveItem !== activeItem) {
                const divElementOld = document.getElementById(prevActiveItem);
                if (divElementOld) {
                 const parentLiOld = divElementOld.closest("li");
                 if (parentLiOld.classList.contains('p-highlight')) {
                  parentLiOld.classList.remove('p-highlight');
                }
                }
              }
        prevActiveItemRef.current = activeItem;
    }, [activeItem]);


  useEffect(() => {
    props.setInsuredSummary(InitialInsuredSummary)
    props.setTransactionSummaryList([])
    props.setAtmsCustomerList([]);
    setSelectedCarrier(null);
    props.setSelectedTransactionIds?.([]);
    props.setTransactionMetadataList?.([]);
    GetCarrierData();
    setTotalTransactionCount?.(0);
  }, [filter.processStatus]);


  useEffect(() => {
    if (props.refreshCounter > 0 || props.autoAdvanceCounter > 0) {
      const shouldAutoAdvance = props.autoAdvanceCounter !== previousAutoAdvanceCounterRef.current;
      previousAutoAdvanceCounterRef.current = props.autoAdvanceCounter || 0;
      // Reset data but preserve the expanded carrier
      props.setInsuredSummary(InitialInsuredSummary)
      props.setTransactionSummaryList([])
      props.setAtmsCustomerList([]);
      props.setSelectedTransactionIds?.([]);
      props.setTransactionMetadataList?.([]);
      fetchCarrierAndSubData(shouldAutoAdvance);
    }
  }, [props.refreshCounter, props.autoAdvanceCounter]);

   
   const findCarrierNameByCode = (companyCode) => {
     if (!companyCode || !carrerResponse?.length) return null;
     const carrier = carrerResponse.find(c => c.companyCode === companyCode);
     return carrier ? carrier.carrierName : null;
   };

  
  const scrollElementWithinPanel = (element: HTMLElement | null) => {
     const panelElement = element?.closest('.left-panel') as HTMLElement | null;
     if (!panelElement || !element) {
       return;
     }

     const panelRect = panelElement.getBoundingClientRect();
     const elementRect = element.getBoundingClientRect();
     panelElement.scrollTo({
       top: panelElement.scrollTop + elementRect.top - panelRect.top,
       behavior: 'smooth'
     });
   };

   const CarrierClick = (carrierElement, shouldScroll = false) => {
     if (carrierElement) {
       const clickEvent = new MouseEvent('click', {
         bubbles: true,
         cancelable: true,
         view: window
       });
       carrierElement.dispatchEvent(clickEvent);
       if (shouldScroll) {
         setTimeout(() => {
           scrollElementWithinPanel(carrierElement);
         }, 150);
       }
     }
   };

   const fetchCarrierAndSubData = async (shouldAutoAdvance = false) => {
     const carrierToExpand = selectedCarrierRef.current;
     const previousCarrierIndex = selectedCarrierIndexRef.current;
     
     const refreshedCarriers = await GetCarrierData(true);
     
     if (carrierToExpand && Array.isArray(refreshedCarriers)) {
       await new Promise(resolve => setTimeout(resolve, 100));
       const currentCarrierIndex = refreshedCarriers.findIndex(carrier => carrier.id === carrierToExpand);
       const currentCarrier = refreshedCarriers[currentCarrierIndex];

       if (currentCarrier) {
         const currentCarrierElement = document.getElementById(`header_${currentCarrier.data.carrierName}`);
         if (currentCarrierElement) {
           CarrierClick(currentCarrierElement, true);
         }

         const transactions = await fetchCarrierSubData(
           carrierToExpand,
           shouldAutoAdvance ? selectedTransactionIndexRef.current : null,
           true
         );
         if (!shouldAutoAdvance || (transactions && transactions.length > 0)) {
           return;
         }
       }

       const nextCarriers = currentCarrierIndex === -1
         ? [
             ...refreshedCarriers.slice(previousCarrierIndex),
             ...refreshedCarriers.slice(0, previousCarrierIndex)
           ]
         : [
             ...refreshedCarriers.slice(currentCarrierIndex + 1),
             ...refreshedCarriers.slice(0, currentCarrierIndex)
           ];
       for (const carrier of nextCarriers) {
         skipSelectedCarrierLoadRef.current = true;
         selectedCarrierRef.current = carrier.id;
         setSelectedCarrier(carrier.id);
         await new Promise(resolve => setTimeout(resolve, 0));
         const carrierElement = document.getElementById(`header_${carrier.data.carrierName}`);
         if (carrierElement) {
           CarrierClick(carrierElement, true);
         }

         const transactions = await fetchCarrierSubData(carrier.id, 0, true);
         if (transactions && transactions.length > 0) {
           return;
         }
       }

       toast.warning("No more unassigned transactions available");
     }
   }


 
  const handleTransactionCheckboxChange = (itemId: string) => {
    const current = selectedTransactionIdsRef.current || [];
    const updated = current.includes(itemId)
      ? current.filter(id => id !== itemId)
      : [...current, itemId];
    props.setSelectedTransactionIds?.(updated);
  };

  ///Template to load submenu, insured information under carrier header data.
  /// Uses refs to avoid stale closure - template is captured when PanelMenu items are built
  const InsuredTemplate = (item, options) => {
    const currentSelectedIds = selectedTransactionIdsRef.current;
    const isChecked = currentSelectedIds?.includes(item.id) || false;
    return (
      <div id={item.id} className={`insured-row flex align-items-center px-2 py-2 cursor-pointer ${item.processStatus === 'E' ? 'text-danger' : ''}`} onClick={() => GetInsuredSummary(item)}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => handleTransactionCheckboxChange(item.id)}
          onClick={(e) => e.stopPropagation()}
          className="multi-select-checkbox"
        />
        {item.seqNum && <span className="seq-num-badge">{item.seqNum}</span>}
        <span id={"Span_" + item.id} data-max-length="10" title={item.label} className={`mx-2 ul-truncate ${item.items && 'font-semibold'}`}>{item.label}</span>
        {item.badge && <Badge className={`ml-auto p-badge p-component p-badge-no-gutter ul-truncate-p-badge ${item.processStatus === 'E' ? 'bg-danger' : ''}`} severity="info" value={item.badge} />}
      </div>
    );
  };

  /// Template to load Menu,carrier information.
  const CarrierTemplate = (item, options) => (

    <>

      {/* {item.data.policyTransactionCount == '8' ? console.log('item', item) : ''} */}
      <div id={"header_" + item.data.carrierName} className="p-panelmenu-header-content headerTemplate" onClick={()=>{setIsClosed(!item.data.isItemExpanded);selectedCarrierRef.current = item.data.companyCode;selectedCarrierIndexRef.current = carrierHeaderlistRef.current.findIndex(carrier => carrier.id === item.data.companyCode);setSelectedCarrier(item.data.companyCode);props.setSelectedTransactionIds?.([]);props.setTransactionMetadataList?.([]); }}  data-pc-section="headercontent">
        <a href="#" className="p-panelmenu-header-link" data-pc-section="headeraction">
          <Icon icon={`${item.data.isItemExpanded ? 'down-arrow' : 'right-arrow'}`}></Icon>
          <span className="p-menuitem-text" data-pc-section="headerlabel" style={{ fontFamily: "&quot;Roboto Condensed sans-serif&quot;" }}>
            <span title={item.data.carrierName} className="truncate">{item.data.carrierName}</span>
            <span className="ml-auto p-badge p-component p-badge-no-gutter">{item.data.policyTransactionCount}</span>
          </span>
        </a>
      </div>
    </>
  );

  const [carrerResponse, setCarrierResponse] = useState<CarrierDataViewHeaderModel[]>([]);

  const handleSearch = () => {
    const validationErrors = validateDynamicFilters(dynamicFilters);
    setFilterValidationErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      GetCarrierData();
    }
  };
  
  ///Fetch carrier data from API
  const GetCarrierData = async (preserveSelectedCarrier = false) => {
    try {
      const validationErrors = validateDynamicFilters(dynamicFilters);
      if (Object.keys(validationErrors).length > 0) {
        setFilterValidationErrors(validationErrors);
        return false;
      }

       if(filter.insuredName.length === 1 && ((filter.insuredName != '-') && (filter.insuredName != '.') && (filter.insuredName != ','))){
         toast.warning("Please enter special characters from ',', '.' and '-' only,or enter more than 3 characters to search Insured Name.");
            return false;
          }
       else if((filter.insuredName.length > 0) && ((filter.insuredName != '-') && (filter.insuredName != '.') && (filter.insuredName != ',')) && (filter.insuredName.length < 3)){
            toast.warning("Please enter at least 3 characters to search Insured Name.");
            return false;
          }

      const policyNumber = getFilterValue('policyNumber');
      if (policyNumber.length > 0 && policyNumber.length < 3) {
        return false;
      }
      const address = getFilterValue('address');
      if (address.length > 0 && address.length < 2) {
        return false;
      }

      const termDateFrom = getFilterValue('effectiveTermDates');
      const termDateTo = getFilterValueTo('effectiveTermDates');
      const transactionEffectiveDateFrom = getFilterValue('transactionEffectiveDate');
      const transactionEffectiveDateTo = getFilterValueTo('transactionEffectiveDate');
      setIsLoading(true);
      setCarriers(initialMenuItemList);
      if (!preserveSelectedCarrier) {
        setSelectedCarrier(null);
      }
      props.setInsuredSummary(InitialInsuredSummary)
      props.setTransactionSummaryList([])
      props.setAtmsCustomerList([]);
      props.setSelectedTransactionIds?.([]);
      props.setTransactionMetadataList?.([]);

      // Keep carrier dropdown populated from a broader source so it does not go empty
      // when the current search criteria returns no carrier records.
      const carrierFilterResponse = await policyMappingDataService.fetchCarrierDataWithCount(
        {
          transactionCode: getActiveTransactionType(),
          processStatus: filter.processStatus,
          policyNumber,
        }
      );

      const totalTransactionCount = carrierFilterResponse.reduce((sum, item) => {
        return sum + Number(item.policyTransactionCount || 0);
      }, 0);

      setTotalTransactionCount(totalTransactionCount);

      setCarrierResponse(carrierFilterResponse);

      const hasFilter =
      filter.processStatus ||
      filter.insuredName ||
      getFilterValue('carrier') ||
      getFilterValue('cs2EntryDate') ||
      transactionEffectiveDateFrom ||
      transactionEffectiveDateTo ||
      getFilterValue('effectiveTermDates') ||
      getFilterValueTo('effectiveTermDates') ||
      getFilterValue('policyNumber') ||
      getFilterValue('address'); 
     
      let response = await policyMappingDataService.fetchCarrierDataWithCount({
        transactionCode: getActiveTransactionType(),
        processStatus: filter.processStatus,
        insuredName: filter.insuredName,
        carrier: getFilterValue('carrier'),
        cs2EntryDate: getFilterValue('cs2EntryDate'),
        transactionEffectiveDateFrom,
        transactionEffectiveDateTo,
        effectiveTermDateFrom: getFilterValue('effectiveTermDates'),
        effectiveTermDateTo: getFilterValueTo('effectiveTermDates'),
        policyNumber: getFilterValue('policyNumber'),
        address: getFilterValue('address'),
      })
      let headerMenu: MenuItem[] = [];
      let sortedheaderMenu: MenuItem[] = [];
      response.forEach((ele: any) => {
        if(ele.carrierName){
        // Set isItemExpanded to true if this carrier matches the last expanded carrier
        ele.isItemExpanded = false;
        let menu: MenuItem = {
          id: ele.companyCode,
          data: ele,
          template: CarrierTemplate,
          items: [],
        }
        headerMenu.push(menu)
      }
      });
      sortedheaderMenu = headerMenu.sort((a, b) => {
        if (a.data.carrierName && b.data.carrierName) {
            return a.data.carrierName.localeCompare(b.data.carrierName); // Sort by carrierName
          }

        return -1;
      }
      );

      if (hasFilter)
      {
        const totalTransactionCount = response.reduce((sum, item) => {
        return sum + Number(item.policyTransactionCount || 0);
      }, 0);

      setTotalTransactionCount(totalTransactionCount);  
      }

      let updateMenuList=sortedheaderMenu
      setCarriers(updateMenuList)
      return updateMenuList;
    }
    catch (error) {
      Logger.logError(constant.UnMatchedPolicy.ERROR_WHILE_FETCHING_CARRIER_MAPPING_LIST, error)
    }
    finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (skipSelectedCarrierLoadRef.current) {
      skipSelectedCarrierLoadRef.current = false;
      return;
    }
    GetInsuredDataByCarrierName(selectedCarrier);
  }, [selectedCarrier]);

  useEffect(() => {
    if (!isClosed) {
      GetInsuredDataByCarrierName(selectedCarrier);
    }},[isClosed])


  // carrierName: string, itemLength?: number
  const GetInsuredDataByCarrierName = ( carrierName: string) => {
    try {
       
    //Setting props for parent
    props.setInsuredSummary(InitialInsuredSummary)
    props.setTransactionSummaryList([])
    props.setAtmsCustomerList([]);
    
      let insuredListLength = carrierHeaderlist.find(e => e["id"] === carrierName)?.["items"]?.length;
      if (insuredListLength <= 0) {
        fetchCarrierSubData(carrierName);
      }
      else {
        let menuI = [...carrierHeaderlist]
        menuI.flatMap(ele => ele.id !== carrierName ? ele.data.isItemExpanded = false : ele.data.isItemExpanded)
        const index = menuI.findIndex((ele) => ele.id === carrierName)
        if (index !== -1) {
          menuI[index]["data"]["isItemExpanded"] = !menuI[index]["data"]["isItemExpanded"]
        }
        let updateCarrierMenuList = menuI;
        setCarriers(updateCarrierMenuList)
      }
     
    }
    catch (error) {
      Logger.logError(constant.UnMatchedPolicy.ERROR_WHILE_FETCHING_CARRIER_MAPPING_LIST, error)
    }
  }


  const fetchCarrierSubData = async (carrierName, autoSelectIndex: number | null = null, shouldScrollCarrier = false) => {
    let companyCode = carrierName;
    let carrierRecord = carrerResponse?.find((ele) => ele.carrierName === carrierName);
    if (carrierRecord) {
      companyCode = carrierRecord.companyCode;
    }
     if(filter.insuredName?.length === 1 && ((filter.insuredName != '-') && (filter.insuredName != '.') && (filter.insuredName != ','))){
         toast.warning("Please enter special characters from ',', '.' and '-' only,or enter more than 3 characters to search Insured Name.");
            return false;
          }
       else if((filter.insuredName !== undefined) && (filter.insuredName?.length > 0) && ((filter.insuredName != '-') && (filter.insuredName != '.') && (filter.insuredName != ',')) && (filter.insuredName?.length < 3)){
            toast.warning("Please enter at least 3 characters to search Insured Name.");
            return false;
          }
    let response = await policyMappingDataService.fetchCustomerDataByCarrierandTransactionCode({
      transactionCode: getActiveTransactionType(),
      processStatus: filter.processStatus,
      insuredName: filter.insuredName,
      carrier: carrierRecord?.carrierName || carrierName,
      cs2EntryDate: getFilterValue('cs2EntryDate'),
      transactionEffectiveDateFrom: getFilterValue('transactionEffectiveDate'),
      transactionEffectiveDateTo: getFilterValueTo('transactionEffectiveDate'),
      effectiveTermDateFrom: getFilterValue('effectiveTermDates'),
      effectiveTermDateTo: getFilterValueTo('effectiveTermDates'),
      policyNumber: getFilterValue('policyNumber'),
      address: getFilterValue('address'),
    });

    if (response.status === 206) {
      toast.warning("More than 500 transaction files exist, but only 500 are currently displayed.");
    }
   
    const metadataList: TransactionMetadata[] = response.data.map((cus) => ({
      id: cus.id,
      insuredsName: cus.insuredsName,
      cycleBusinessPurposeCode: cus.cycleBusinessPurposeCode,
      policyNumber: cus.policyNumber,
      policyEffectiveDate: cus.policyEffectiveDate,
      policyExpirationDate: cus.policyExpirationDate,
      seqNum: cus.seqNum,
      transactionDate: cus.transactionDate,
      lineOfBusinessCode: cus.lineOfBusinessCode,
    }));
    props.setTransactionMetadataList?.(metadataList);

    const sortedSubheader = response.data.map(cus => ({
      id: cus.id,
      label: cus.insuredsName,
      tooltip: cus.insuredsName,
      data: cus.cycleBusinessPurposeCode,
      badge: cus.cycleBusinessPurposeCode,
      items: [],
      processStatus: cus.processStatus,
      seqNum: cus.seqNum,
      template: InsuredTemplate
    })).sort((a, b) => {
      if (a.label && b.label) {
        const nameCompare = a.label.localeCompare(b.label);
        if (nameCompare !== 0) return nameCompare;
        return String(a.seqNum || '').localeCompare(String(b.seqNum || ''), undefined, { numeric: true });
      }

      return -1;
    });

    setCarriers((prevStateHeaderList) => {
      let menuI = [...prevStateHeaderList]
      menuI.flatMap(ele=>ele.id!==carrierName?ele.data.isItemExpanded=false:ele.data.isItemExpanded)
      const index = menuI.findIndex((ele) => ele.id === carrierName)
      if (index !== -1) {
        menuI[index]["items"] = []
        menuI[index]["items"] = sortedSubheader
        menuI[index]["data"]["isItemExpanded"] = true
      }
      let updateCarrierMenuList=menuI;
      return updateCarrierMenuList
    })

    if (shouldScrollCarrier) {
      setTimeout(() => {
        const carrierElement = document.getElementById(`header_${carrierName}`);
        scrollElementWithinPanel(carrierElement);
      }, 0);
    }

    if (autoSelectIndex !== null && sortedSubheader.length > 0) {
      const nextTransactionIndex = autoSelectIndex >= sortedSubheader.length ? 0 : autoSelectIndex;
      GetInsuredSummary(sortedSubheader[nextTransactionIndex], nextTransactionIndex, true);
    }

    return sortedSubheader;
  }


  const GetInsuredSummary = async (insuredData: CustomerDataSnapshot, selectedIndex?: number, shouldScroll = false) => {
    try {
      const selectedCarrierItems = carrierHeaderlistRef.current.find(item => item.id === selectedCarrier)?.items || [];
      selectedTransactionIndexRef.current = selectedIndex ?? selectedCarrierItems.findIndex(item => item.id === insuredData.id);
      setHighlighted(insuredData.id, shouldScroll)
      props.setInsuredSummary(insuredData)
    }
    catch (error) {
      Logger.logError(constant.UnMatchedPolicy.ERROR_WHILE_FETCHING_CARRIER_MAPPING_LIST, error)
    }
  }
 const setHighlighted = (itemId, shouldScroll = false) => {
      if (shouldScroll) {
        setTimeout(() => {
          scrollElementWithinPanel(document.getElementById(itemId));
        }, 0);
      }
            if (activeItem === itemId) {
                return;
            }
            else{
              setActiveItem(itemId);
              const divElement = document.getElementById(itemId);
              if (divElement) {
                const parentLi = divElement.closest("li");
                parentLi?.classList.add('p-highlight');
              }
            }
    };

  const changeProcessStatus = (event, status) => {
  if (event.currentTarget.checked) {
   setTransactionFilter({
      ...filter,
      processStatus: [...filter.processStatus.filter(s => s !== 'M'), status]
    });
  } else {
    const updatedStatuses = filter.processStatus.filter(s => s !== status);
    setTransactionFilter({
      ...filter,
      processStatus: updatedStatuses.length > 0 ? updatedStatuses : ['M']
    });
  }
};

  return (
    <>
    <Spinner loading={isLoading} size={10} sizeUnit={'%'} type="SyncLoader" />
      <div className="card">
        <div className="row">&nbsp;</div>
        <div className="card-body" style={{ minHeight: '400px' }}>
           {props.hasUIComponentViewAccess && 
            <>
            <div className="row"> 
              <div className="col-md-8">
                  <input
                    className="form-control"
                    type="text"
                    value={filter.insuredName}
                    maxLength={150}
                    onChange={(event) => onFieldChange('insuredName', event.currentTarget.value)}
                    onKeyDown={(e) => {
                        if (e.key == 'Enter') {
                          handleSearch();
                          e.preventDefault();
                        }
                      }}
                    placeholder={"Search Insured Name"}
                />
              </div>
               <div className="col-md-2">
                  <Button type="button" onClick={handleSearch} variant="outline-primary">{label.UnmatchedPolicy.SEARCH.toLocaleUpperCase()}</Button>
              </div>
            </div>
            
          </>
}
          <div className="row">

            <div className="col-lg-12 overflow-auto left-panel">
              <div className="filter-actions-row mt-1">
              <div className="col-checkboxes">
                <Checkbox
                  id='chkShowOnHold'
                  label={label.UnmatchedPolicy.SHOW_ON_HOLD}
                  checked={filter.processStatus.includes('H') ? true : false}
                  onChange={(event) => changeProcessStatus(event, 'H')}
                  disabled={!props.hasUIComponentReadAccess}
                  className="formcheck-nopadding d-inline-block"
                  checkboxStyle={{ position: "relative", marginLeft: "0px" }}
                  labelStyle={{ marginLeft: "6px" }}
                />
                <Checkbox
                  id='chkShowOnError'
                  label={label.UnmatchedPolicy.SHOW_ERROR}
                  checked={filter.processStatus.includes('E') ? true : false}
                  onChange={(event) => changeProcessStatus(event, 'E')}
                  disabled={!props.hasUIComponentReadAccess}
                  className="formcheck-nopadding d-inline-block ml-2"
                  checkboxStyle={{ position: "relative", marginLeft: "0px" }}
                  labelStyle={{ marginLeft: "6px" }}
                />
              </div>
              <button
                type="button"
                className="btn btn-link p-0 add-filter-link"
                onClick={addFilterRow}
                disabled={!props.hasUIComponentReadAccess || dynamicFilters.length >= FILTER_OPTIONS.length}
              >
                + Add Filter
              </button>
            </div>
            <div className="dynamic-filters mt-2">
              {dynamicFilters.map((df) => {
                const usedTypes = dynamicFilters.filter(f => f.id !== df.id && f.filterType).map(f => f.filterType);
                const validationErrors = filterValidationErrors[df.id];
                return (
                <div key={df.id} className="filter-row">
                  <select
                    className="form-control form-control-sm filter-dropdown"
                    value={df.filterType}
                    onChange={(e) => updateFilterRow(df.id, 'filterType', e.target.value)}
                  >
                    <option value="">Select Filter</option>
                    {[...FILTER_OPTIONS].sort((a, b) => a.label.localeCompare(b.label)).map(opt => (
                      <option key={opt.value} value={opt.value} disabled={usedTypes.includes(opt.value)}>{opt.label}</option>
                    ))}
                  </select>
                  <div className="filter-input">
                    {(df.filterType === 'carrier') && (
                      <Select
                        id={`carrier-filter-${df.id}`}
                        controlId={`carrier-filter-${df.id}`}
                        options={[...carrerResponse]
                          .filter(c => c.carrierName)
                          .sort((a, b) => (a.carrierName || '').localeCompare(b.carrierName || ''))
                          .map(c => ({ label: c.carrierName || '', value: c.companyCode || '', isInactive: false }))}
                        defaultSelected={df.value ? [{ label: carrerResponse.find(c => c.companyCode === df.value)?.carrierName || '', value: df.value, isInactive: false }] : []}
                        onChange={(values) => updateFilterRow(df.id, 'value', (values[0] as string) || '')}
                        placeholder="Search Carrier"
                        multiple={false}
                        showTitle={true}
                        className="filter-autocomplete"
                      />
                    )}
                    {(df.filterType === 'transactions') && (
                      <Select
                        id={`txn-filter-${df.id}`}
                        controlId={`txn-filter-${df.id}`}
                        options={transactionTypes}
                        defaultSelected={transactionTypes.filter(option => option.value === df.value)}
                        label={!df.value ? '' : undefined}
                        onChange={(values) => {
                          const selectedValue = (values[0] as string);
                          onFieldChange('transactionType', selectedValue);
                          updateFilterRow(df.id, 'value', selectedValue);
                        }}
                        placeholder="Search Transaction"
                        multiple={false}
                        showTitle={true}
                        className="filter-autocomplete"
                        disabled={!props.hasUIComponentReadAccess}
                      />
                    )}
                    {(df.filterType === 'cs2EntryDate') && (
                      <input
                        className={`form-control form-control-sm ${validationErrors?.value ? 'is-invalid' : ''}`}
                        type="date"
                        value={df.value}
                        onChange={(e) => updateFilterRow(df.id, 'value', e.currentTarget.value, e.currentTarget.validity.badInput)}
                        onBlur={(e) => updateFilterRow(df.id, 'value', e.currentTarget.value, e.currentTarget.validity.badInput)}
                      />
                    )}
                    {(df.filterType === 'policyNumber') && (
                      <>
                        <input
                          className={`form-control form-control-sm ${validationErrors?.value ? 'is-invalid' : ''}`}
                          type="text"
                          value={df.value}
                          onChange={(e) => updateFilterRow(df.id, 'value', e.target.value)}
                        />
                      </>
                    )}
                    {(df.filterType === 'address') && (
                      <>
                        <input
                          className={`form-control form-control-sm ${validationErrors?.value ? 'is-invalid' : ''}`}
                          type="text"
                          value={df.value}
                          onChange={(e) => updateFilterRow(df.id, 'value', e.target.value)}
                        />
                      </>
                    )}
                    {(df.filterType === 'effectiveTermDates' || df.filterType === 'transactionEffectiveDate') && (
                      <input
                        className="form-control form-control-sm"
                        type="text"
                        value="Date Range"
                        disabled
                      />
                    )}
                    {!df.filterType && (
                      <input
                        className="form-control form-control-sm"
                        type="text"
                        placeholder="Select a filter first"
                        disabled
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger filter-remove-btn"
                    onClick={() => removeFilterRow(df.id)}
                    title="Remove filter"
                  >
                    <i className="pi pi-times" />
                  </button>
                  {(df.filterType === 'effectiveTermDates' || df.filterType === 'transactionEffectiveDate') && (
                    <div className="date-range-inputs">
                      <div className="date-range-field">
                        <input
                          className={`form-control form-control-sm ${validationErrors?.value ? 'is-invalid' : ''}`}
                          type="date"
                          value={df.value}
                          onChange={(e) => updateFilterRow(df.id, 'value', e.currentTarget.value, e.currentTarget.validity.badInput)}
                          onBlur={(e) => updateFilterRow(df.id, 'value', e.currentTarget.value, e.currentTarget.validity.badInput)}
                        />
                      </div>
                      <span className="date-range-sep">to</span>
                      <div className="date-range-field">
                        <input
                          className={`form-control form-control-sm ${validationErrors?.valueTo ? 'is-invalid' : ''}`}
                          type="date"
                          value={df.valueTo}
                          onChange={(e) => updateFilterRow(df.id, 'valueTo', e.currentTarget.value, e.currentTarget.validity.badInput)}
                          onBlur={(e) => updateFilterRow(df.id, 'valueTo', e.currentTarget.value, e.currentTarget.validity.badInput)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )})}
            </div>
              <div className="card flex justify-content-center ">
                <PanelMenu model={carrierHeaderlist}
                  expandIcon="pi pi-chevron-up"
                  collapseIcon="pi pi-chevron-right"
                  className="w-full md:w-20rem"
                />
              </div>
            </div>
            <div className="total-transaction-count">Total: {totalTransactionCount}</div>
          </div>
        </div>
      </div>
    </>
  )
}
export default UnmatchedLeftPanel;